import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Home } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Crescent } from "../components/Decor";
import { MB_FOOD_POLICY_URL, MB_FOOD_APPLICATION_URL } from "../lib/data";
import api from "../lib/api";

const MAX_ATTEMPTS = 6;
const INTERVAL = 2500;

export default function PaymentStatus() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | success | failed | timeout
  const [info, setInfo] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    if (startedRef.current) return;
    startedRef.current = true;

    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo(data); setState("success"); return;
        }
        if (data.status === "expired") { setState("failed"); return; }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) { setState("timeout"); return; }
        setTimeout(poll, INTERVAL);
      } catch {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) { setState("timeout"); return; }
        setTimeout(poll, INTERVAL);
      }
    };
    poll();
  }, [sessionId]);

  const amount = info ? (info.amount_total / 100).toFixed(2) : null;

  return (
    <div data-testid="payment-status-page">
      <Navbar />
      <section className="sunburst min-h-[70vh] flex items-center justify-center px-4 py-20 relative">
        <div className="absolute inset-0 sunburst-green pointer-events-none" />
        <div className="stamp p-3 w-full max-w-lg relative">
          <div className="stamp-inner p-8 md:p-10 bg-cream text-center">
            <div className="flex justify-center mb-4"><Crescent size={56} /></div>

            {state === "polling" && (
              <>
                <Loader2 size={44} className="animate-spin text-rust mx-auto mb-4" data-testid="ps-loading" />
                <h1 className="font-display font-bold text-forest text-3xl mb-2">Confirming your payment…</h1>
                <p className="text-forest-light">Please hold on a moment — don't close this page.</p>
              </>
            )}

            {state === "success" && (
              <div data-testid="ps-success">
                <div className="postmark animate-stamp-in w-32 h-32 flex flex-col items-center justify-center mx-auto mb-5">
                  <CheckCircle2 size={36} />
                  <span className="text-[0.55rem] mt-1">Paid · Pak Fest</span>
                </div>
                <h1 className="font-display font-bold text-forest text-3xl mb-2">Payment Received!</h1>
                {amount && <p className="numeral text-rust text-4xl mb-3">${amount} CAD</p>}
                <p className="text-forest-light mb-4">
                  Thank you! A receipt and confirmation have been emailed to you.
                </p>
                <div className="border-2 border-rust bg-cream-muted p-4 text-sm text-forest-light text-left">
                  <strong>Please note:</strong> Your participation is subject to CPTCA's review of applicable
                  health requirements and policies. If your application cannot be approved, your payment will be
                  fully refunded.
                </div>
                {info?.is_food_vendor && (
                  <div className="border-2 border-rust bg-cream-muted p-4 text-sm text-forest-light text-left mt-4" data-testid="ps-mbhealth">
                    <p className="font-bold text-rust mb-2">Action Required — Manitoba Health (Food Vendors)</p>
                    <ol className="list-decimal pl-5 space-y-1">
                      <li>Review the <a href={MB_FOOD_POLICY_URL} target="_blank" rel="noopener noreferrer" className="text-rust underline font-semibold" data-testid="ps-mb-policy">Manitoba Health Food Safety Policy (PDF)</a>.</li>
                      <li>Complete the <a href={MB_FOOD_APPLICATION_URL} target="_blank" rel="noopener noreferrer" className="text-rust underline font-semibold" data-testid="ps-mb-application">Temporary Food Service Application (PDF)</a>.</li>
                      <li>Email your completed application with a copy of your food licence / permit to <a href="mailto:cptcamb@gmail.com" className="text-rust underline font-semibold">cptcamb@gmail.com</a>.</li>
                    </ol>
                    <p className="mt-2 text-forest-light/80">Your booth cannot be finalized until these documents are received and approved.</p>
                  </div>
                )}
              </div>
            )}

            {(state === "failed" || state === "timeout") && (
              <div data-testid="ps-failed">
                {state === "failed" ? <XCircle size={44} className="text-rust mx-auto mb-4" /> : <Loader2 size={44} className="text-rust mx-auto mb-4" />}
                <h1 className="font-display font-bold text-forest text-3xl mb-2">
                  {state === "failed" ? "Payment Not Completed" : "Still Processing"}
                </h1>
                <p className="text-forest-light mb-2">
                  {state === "failed"
                    ? "Your payment wasn't completed. If you were charged, please contact us at cptcamb@gmail.com."
                    : "We couldn't confirm your payment yet. If money was deducted, you'll receive an email shortly — or contact cptcamb@gmail.com."}
                </p>
              </div>
            )}

            <Link to="/" className="btn-forest px-6 py-3 text-sm inline-flex items-center gap-2 mt-7" data-testid="ps-home">
              <Home size={16} /> Back Home
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
